import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  shallowEqual, useDispatch, useSelector
} from 'react-redux';

import { clearPatchOrderedParts } from '../../actions/coilovers/orderedParts/patchOrderedParts';
import correctionOrderedParts from '../../thunks/coilovers/orderedParts/correctionOrderedParts';
import Modal from '../Modal/Modal';
import Text from '../Text/Text';
import Input from '../Input/Input';
import Alert from '../Alert/Alert';
import AlertType from '../../common/alertType';
import Badge from '../Badge/Badge';
import ButtonTypes from '../../constants/buttonTypes';
import ConfirmModal from '../ConfirmModal/ConfirmModal';

const CorrectionOrderedPartsModal = ({ visible, toggleVisible, item }) => {
  const [confirmVisible, setConfirmVisible] = useState(false);
  const dispatch = useDispatch();
  const {
    patchOrderedParts: {
      loading, error: patchError, data: patchedData
    }
  } = useSelector((state) => ({
    patchOrderedParts: state?.patchOrderedParts,
  }), shallowEqual);

  const {
    _id,
    ordered_part_id: partId,
    ordered_part_po: po,
    ordered_part_mpn: orderedMpn,
    ordered_part_qty_in_order: orderedQty,
    ordered_part_qty_delivered: deliveredQty,
  } = item;

  const {
    values: {
      partOrderedQty,
      partDeliveredQty,
      partCorrectionQty,
    },
    handleSubmit,
    setValues,
    isValid,
    handleBlur,
    handleChange,
    touched,
    errors
  } = useFormik({
    initialValues: {
      partOrderedQty: 0,
      partDeliveredQty: 0,
      partCorrectionQty: 0,
    },
    onSubmit: (values) => {
      dispatch(correctionOrderedParts({
        _id,
        formData: {
          ordered_part_qty_correction: parseInt(values?.partCorrectionQty, 10),
        },
      }));
    },
    validationSchema: Yup.object({
      partOrderedQty: Yup.number().required('Required'),
      partDeliveredQty: Yup.number().required('Required'),
      partCorrectionQty: Yup.number()
        .required('Required')
        .test('min-new-delivered', 'New delivered cannot be less than 0', (value, context) => {
          const delivered = parseInt(context.parent.partDeliveredQty, 10) || 0;
          const correction = parseInt(value, 10) || 0;
          return delivered + correction >= 0;
        }),
    })
  });

  useEffect(() => {
    if (Object.keys(patchedData).length) {
      dispatch(clearPatchOrderedParts());
      toggleVisible();
    }
  }, [patchedData]);

  useEffect(() => {
    setValues({
      partOrderedQty: parseInt(orderedQty, 10) || 0,
      partDeliveredQty: parseInt(deliveredQty, 10) || 0,
      partCorrectionQty: 0,
    }, true);
  }, [item]);

  const nextDeliveredQty = (parseInt(partDeliveredQty, 10) || 0) + (parseInt(partCorrectionQty, 10) || 0);

  return (
    <>
      <Modal
        visible={visible}
        toggleVisible={() => {
          toggleVisible();
          dispatch(clearPatchOrderedParts());
        }}
        title="Correction part"
        subTitle={`id: ${partId} | po: ${po} | mpn: ${orderedMpn}`}
        onSave={() => setConfirmVisible(true)}
        canSave={!isValid || nextDeliveredQty < 0}
        loading={loading}
      >
        <div className="customScroll w-[500px] px-[10px]">
          <div className="flex flex-row items-center">
            <Text text="New delivered:  " muted styleContainer="mr-[8px]" />
            <Badge
              text={`${nextDeliveredQty}`}
              type={ButtonTypes.LIGHT_SUCCESS}
            />
          </div>
          <br />
          <div className="flex flex-row justify-between">
            <div className="flex1">
              <Text text="Ordered:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                type="number"
                name="partOrderedQty"
                value={`${partOrderedQty}`}
                disabled
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Delivered:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                type="number"
                name="partDeliveredQty"
                value={`${partDeliveredQty}`}
                disabled
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Correction:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                type="number"
                name="partCorrectionQty"
                value={`${partCorrectionQty}`}
                placeholder="Correction qty"
                min={Number.MIN_SAFE_INTEGER}
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partCorrectionQty && errors?.partCorrectionQty}
              />
            </div>
          </div>
          {nextDeliveredQty < 0 && (
            <Alert text="New delivered cannot be less than 0" type={AlertType.warning} />
          )}
          {patchError && <Alert text={patchError} type={AlertType.warning} />}
        </div>
      </Modal>
      <ConfirmModal
        onConfirm={() => {
          setConfirmVisible(false);
          handleSubmit();
        }}
        visible={confirmVisible}
        onCancel={() => {
          setConfirmVisible(false);
        }}
      />
    </>
  );
};

CorrectionOrderedPartsModal.propTypes = {
  toggleVisible: PropTypes.func,
  visible: PropTypes.bool,
  item: PropTypes.shape({
    _id: PropTypes.string,
    ordered_part_id: PropTypes.number,
    ordered_part_po: PropTypes.string,
    ordered_part_mpn: PropTypes.string,
    ordered_part_qty_in_order: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ordered_part_qty_delivered: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })
};

CorrectionOrderedPartsModal.defaultProps = {
  toggleVisible: () => {},
  visible: false,
  item: {}
};

export default CorrectionOrderedPartsModal;
